import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Sparkles } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { getPlan, approvePlan, generatePlan, getDashboardSummary } from "@/lib/api";

export default function PianoMarketing() {
  const [savedPlan, setPlan] = useState(null);
  const [goal, setGoal] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [approving, setApproving] = useState(false);
  const [campaignDraft, setCampaignDraft] = useState(null);

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [budget, setBudget] = useState("");
  const load = () => {
    setLoading(true);
    setLoadError(false);
    return Promise.all([getPlan(), getDashboardSummary()])
      .then(([p, summary]) => { setPlan(p); setGoal(summary.main_goal); })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };
  const priority = goal?.title?.trim();
  const oldContent = JSON.stringify(savedPlan || {});
  const unrelatedTactics = (
    /newsletter/i.test(oldContent) && !/newsletter/i.test(priority || "")
  ) || (
    /(?:sconto\s+(?:(?:del|di)\s+)?10\s*%|10\s*%\s+di\s+sconto)/i.test(oldContent)
    && !/(?:sconto\s+(?:(?:del|di)\s+)?10\s*%|10\s*%\s+di\s+sconto)/i.test(priority || "")
  );
  const proposedView = Boolean(priority && !savedPlan?.approved && (
    !savedPlan || savedPlan.commercial_goal?.trim() !== priority || unrelatedTactics
  ));
  // View only: retain the saved draft, without generating, saving or approving a replacement.
  const plan = proposedView
    ? { commercial_goal: priority, goal_id: goal.id, status: "proposed", approved: false }
    : savedPlan;
  const archivedPlan = proposedView && savedPlan
    ? { ...savedPlan, status: "archived", approved: false }
    : null;
  const generate = async () => {
    setGenerating(true);
    try { setPlan(await generatePlan({ budget_monthly: budget ? Number(budget) : null })); toast.success("Piano pronto: controllalo e approvalo"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Generazione non riuscita"); } finally { setGenerating(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async () => {
    if (proposedView) return;
    setApproving(true);
    try {
      const result = await approvePlan();
      setCampaignDraft(result.campaign_draft || null);
      if (result.campaign_draft?.saved) toast.success(result.campaign_draft.message);
      else toast.info(result.campaign_draft?.message || "Piano approvato. Creazione della bozza non confermata.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio della bozza non confermato. Riprova.");
    } finally {
      setApproving(false);
    }
  };

  if (loading) return <Skeleton className="h-96 bg-white/5" />;
  if (loadError) return (
    <div className="nadia-card p-8 space-y-3" data-testid="plan-load-error">
      <p className="text-sm text-slate-400">Non è stato possibile caricare il piano e la priorità di Panoramica.</p>
      <Button onClick={load} data-testid="plan-load-retry">Riprova</Button>
    </div>
  );
  if (!plan) return (
    <div className="nadia-card p-8 text-center space-y-3" data-testid="plan-empty">
      <h2 className="font-heading text-lg font-semibold text-slate-100">Nessun piano marketing ancora</h2>
      <p className="text-sm text-slate-400 max-w-xl mx-auto">Nadia costruisce il piano partendo dal Business Brain e dall'obiettivo scelto: pubblico, area, mix canali, budget, concetto di campagna, angolo creativo, CTA, timeline e KPI. Nessun risultato inventato.</p>
      <div className="flex flex-col sm:flex-row gap-2 justify-center items-center">
        <input value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="Budget mensile € (opzionale)" className="h-10 w-56 rounded-xl bg-white/[0.03] border border-white/10 px-3 text-sm text-slate-100" data-testid="plan-budget-input" />
        <Button onClick={generate} disabled={generating} className="bg-gradient-to-r from-cyan-400 to-violet-500 text-slate-950 font-semibold" data-testid="plan-generate-button">{generating ? "Nadia sta costruendo il piano…" : "Genera il piano con Nadia"}</Button>
      </div>
    </div>
  );

  return (
    <>
      <PageHeader
        eyebrow="Piano Marketing"
        title={proposedView ? "Il piano da costruire sulla tua priorità" : "Il piano che ho preparato per te"}
        subtitle={proposedView ? "Priorità ripresa da Panoramica. La proposta è da completare, non è approvata né eseguita." : "Approvalo per far partire l'esecuzione, o chiedimi modifiche in chat."}
        actions={
          proposedView ? (
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-cyan-500/10 border border-cyan-500/25 text-cyan-300 text-sm font-semibold" data-testid="plan-proposed-status" data-status={plan.status} data-approved={plan.approved}>
              Proposto · da completare
            </span>
          ) : plan.approved ? (
            <>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 text-sm font-semibold">
              <CheckCircle2 size={16} /> Approvato
            </span>
            <Button onClick={approve} disabled={approving} variant="outline" data-testid="plan-create-campaign-draft">
              {approving ? "Salvataggio…" : "Crea / mostra bozza in Campagne"}
            </Button>
            </>
          ) : (
            <Button
              onClick={approve}
              disabled={approving}
              className="bg-gradient-to-r from-cyan-400 to-violet-500 text-slate-950 hover:from-cyan-300 hover:to-violet-400 font-semibold"
              data-testid="approve-plan-button"
            >
              <Sparkles size={16} className="mr-2" /> Approva piano
            </Button>
          )
        }
      />

      {campaignDraft && (
        <div className="nadia-card p-4 mb-4 text-sm text-slate-200" role="status" data-testid="plan-campaign-result">
          <p data-testid="plan-campaign-message">{campaignDraft.message}</p>
          {campaignDraft.saved && <>
            <p className="text-xs text-slate-400 mt-2 break-all" data-testid="plan-campaign-id">ID: {campaignDraft.id}</p>
            <Link to="/app/campagne" className="inline-block mt-2 text-cyan-300 hover:text-cyan-200 underline" data-testid="plan-campaign-link">Apri Campagne</Link>
          </>}
        </div>
      )}
      {proposedView && <p className="text-sm text-slate-400 mb-4" data-testid="plan-proposed-notice">
        Questa è una proposta nella vista, non un nuovo piano salvato o generato. Offerta, canali, budget e materiali restano da definire: non vengono ripresi dalla bozza precedente.
      </p>}
      <PlanDetails plan={plan} testId="current-plan" />
      {archivedPlan && (
        <details className="nadia-card p-5 mt-6" data-testid="plan-archive" data-status={archivedPlan.status} data-approved={archivedPlan.approved}>
          <summary className="cursor-pointer text-sm font-semibold text-slate-400" data-testid="plan-archive-toggle">Archivio · bozza precedente non approvata</summary>
          <p className="text-sm text-slate-400 my-4" data-testid="plan-archive-notice">Contenuti conservati, non selezionati per la priorità corrente. Nessuna approvazione o esecuzione.</p>
          <PlanDetails plan={archivedPlan} testId="archived-plan" />
        </details>
      )}
    </>
  );
}

function PlanDetails({ plan, testId }) {
  return (
    <section data-testid={testId}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Row testId={`${testId}-goal`} label="Obiettivo commerciale" value={plan.commercial_goal} />
        <Row testId={`${testId}-offer`} label="Offerta" value={plan.offer} />
        <Row testId={`${testId}-audience`} label="Pubblico" value={plan.audience} />
        <Row testId={`${testId}-area`} label="Area geografica" value={plan.geographic_area} />
        <Row testId={`${testId}-channels`} label="Canali" value={plan.channels?.join(" · ")} />
        <Row testId={`${testId}-budget`} label="Budget mensile" value={plan.budget_monthly != null ? `€${plan.budget_monthly}` : "Da definire"} />
        <Row testId={`${testId}-duration`} label="Durata" value={plan.duration_days ? `${plan.duration_days} giorni` : "Da definire"} />
        {plan.landing_page && <Row testId={`${testId}-landing`} label="Landing page" value={plan.landing_page} link />}
        <Row testId={`${testId}-cta`} label="Call to Action" value={plan.cta} highlight />
        {plan.measurement_event && <Row testId={`${testId}-measurement`} label="Evento misurato" value={plan.measurement_event} />}
        {plan.campaign_concept && <Row testId={`${testId}-concept`} label="Concetto di campagna" value={plan.campaign_concept} />}
        {plan.creative_angle && <Row testId={`${testId}-angle`} label="Angolo creativo" value={plan.creative_angle} />}
        {plan.budget_allocation?.length > 0 && <Row testId={`${testId}-allocation`} label="Allocazione budget" value={plan.budget_allocation.map((b) => `${b.channel} ${b.share_pct}%`).join(" · ")} />}
        {plan.kpis?.length > 0 && <Row testId={`${testId}-kpis`} label="KPI da monitorare" value={plan.kpis.join(" · ")} />}
      </div>
      {plan.timeline?.length > 0 && (
        <div className="nadia-card p-5 mt-4" data-testid={testId === "current-plan" ? "plan-timeline" : `${testId}-timeline`}>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Timeline</div>
          <ol className="space-y-1 text-sm text-slate-200">{plan.timeline.map((t, i) => <li key={i} data-testid={`${testId}-timeline-${i}`}><span className="text-cyan-300 font-semibold">Settimana {t.week}</span> · {t.activity}</li>)}</ol>
        </div>
      )}

      <div className="nadia-card p-5 mt-4" data-testid={`${testId}-copy`}>
        <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">
          Copy dell'annuncio
        </div>
        <p className="text-slate-100 text-sm leading-relaxed italic">{plan.copy ? `"${plan.copy}"` : "Da definire"}</p>
      </div>
    </section>
  );
}

function Row({ testId, label, value, highlight, link }) {
  return (
    <div className="nadia-card p-5" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">
        {label}
      </div>
      {link ? (
        <a href={value} target="_blank" rel="noreferrer" className="text-cyan-300 hover:underline text-sm break-all" data-testid={`${testId}-link`}>
          {value}
        </a>
      ) : (
        <div
          className={`text-sm ${highlight ? "font-heading font-bold text-lg text-slate-50" : "text-slate-100"}`}
        >
          {value || "Da definire"}
        </div>
      )}
    </div>
  );
}
